import type * as AgGrid from "ag-grid-community";

type UuidTextFilterModel = {
  filterType: "text";
  type: "equals";
  filter: string;
};

type UuidTextFilterLocales = {
  label?: string;
  placeholder?: string;
  invalid_uuid?: string;
  apply_button?: string;
  reset_button?: string;
};

type UuidTextFilterParams = AgGrid.IFilterParams & {
  locales?: UuidTextFilterLocales;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class UuidTextFilter implements AgGrid.IFilterComp {
  private params!: UuidTextFilterParams;
  private gui!: HTMLDivElement;
  private input!: HTMLInputElement;
  private errorEl!: HTMLDivElement;
  private currentModel: UuidTextFilterModel | null = null;
  private hidePopup?: () => void;
  private colId!: string;

  init(params: UuidTextFilterParams): void {
    this.params = params;
    const locales = this.params.locales || {};
    this.colId = this.params.column?.getColId?.() || this.params.colDef?.field || "uuid";

    this.gui = document.createElement("div");
    this.gui.className = "ag-filter";
    this.gui.style.minWidth = "380px";

    const form = document.createElement("form");
    form.className = "ag-filter-wrapper ag-focus-managed";
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.apply();
    });

    const bodyWrapper = document.createElement("div");
    bodyWrapper.className = "ag-filter-body-wrapper ag-simple-filter-body-wrapper";
    bodyWrapper.setAttribute("data-ref", "eFilterBody");
    bodyWrapper.style.flex = "0 0 auto";

    const body = document.createElement("div");
    body.className = "ag-filter-body";
    body.setAttribute("role", "presentation");
    body.setAttribute("aria-hidden", "false");

    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.placeholder = locales.placeholder || "123e4567-e89b-12d3-a456-426614174000";
    this.input.className = "ag-input-field-input ag-text-field-input";
    this.input.setAttribute(
      "aria-label",
      locales.label || this.params.colDef?.headerName || this.params.column?.getColDef?.().headerName || "UUID",
    );
    this.input.style.fontFamily = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace";
    this.input.style.width = "100%";

    this.errorEl = document.createElement("div");
    this.errorEl.className = "ag-input-field-label";
    this.errorEl.style.display = "none";
    this.errorEl.style.color = "#dc2626";
    this.errorEl.style.fontSize = "12px";
    this.errorEl.style.marginTop = "6px";

    const inputField = document.createElement("div");
    inputField.className = "ag-labeled ag-label-align-left ag-text-field ag-input-field ag-filter-filter";

    const inputWrapper = document.createElement("div");
    inputWrapper.className = "ag-wrapper ag-input-wrapper ag-text-field-input-wrapper";
    inputWrapper.setAttribute("data-ref", "eWrapper");
    inputWrapper.setAttribute("role", "presentation");
    inputWrapper.appendChild(this.input);
    inputField.appendChild(inputWrapper);

    body.append(inputField, this.errorEl);
    bodyWrapper.appendChild(body);

    const applyBtn = document.createElement("button");
    applyBtn.type = "button";
    applyBtn.className = "ag-button ag-standard-button ag-filter-apply-panel-button";
    applyBtn.textContent = locales.apply_button || "Apply";
    applyBtn.addEventListener("click", () => this.apply());

    const resetBtn = document.createElement("button");
    resetBtn.type = "button";
    resetBtn.className = "ag-button ag-standard-button ag-filter-apply-panel-button";
    resetBtn.textContent = locales.reset_button || "Reset";
    resetBtn.addEventListener("click", () => this.reset());

    const applyPanel = document.createElement("div");
    applyPanel.className = "ag-filter-apply-panel";
    applyPanel.append(resetBtn, applyBtn);

    form.append(bodyWrapper, applyPanel);
    this.gui.appendChild(form);

    this.hidePopup = () => {
      requestAnimationFrame(() => {
        const filterMenu = this.gui.closest(".ag-menu.ag-filter-menu");
        filterMenu?.remove();
      });
    };

    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.apply();
      }
    });
  }

  getGui(): HTMLElement {
    return this.gui;
  }

  isFilterActive(): boolean {
    return !!this.currentModel?.filter;
  }

  getModel(): UuidTextFilterModel | null {
    return this.currentModel;
  }

  setModel(model: UuidTextFilterModel | null): void {
    this.currentModel = model;
    const value = model?.filter ?? "";
    if (this.input) this.input.value = value;
    this.hideError();
  }

  doesFilterPass(): boolean {
    return true;
  }

  private apply(): void {
    const raw = this.input.value.trim();
    if (!raw) {
      const hadActive = this.currentModel !== null;
      this.currentModel = null;
      this.hideError();
      if (hadActive) this.params.filterChangedCallback();
      this.hidePopup?.();
      return;
    }

    if (!UUID_RE.test(raw)) {
      this.showError(
        this.params.locales?.invalid_uuid || "Invalid UUID format. Example: 123e4567-e89b-12d3-a456-426614174000",
      );
      return;
    }

    const nextModel: UuidTextFilterModel = { filterType: "text", type: "equals", filter: raw };
    const changed = this.currentModel?.filter !== nextModel.filter;
    this.currentModel = nextModel;
    this.hideError();
    if (changed) this.params.filterChangedCallback();
    this.hidePopup?.();
  }

  private reset(): void {
    const hadActive = this.currentModel !== null;
    const hadDraft = this.input.value.trim().length > 0;
    const gridHadActive =
      !!this.params.api?.getFilterModel?.()?.[this.colId] ||
      !!this.params.api?.getFilterModel?.()?.[this.params.colDef?.field || ""];
    this.input.value = "";
    this.currentModel = null;
    this.hideError();
    if (hadActive || gridHadActive) this.params.filterChangedCallback();
    this.hidePopup?.();
  }

  private showError(message: string): void {
    this.errorEl.textContent = message;
    this.errorEl.style.display = "block";
  }

  private hideError(): void {
    this.errorEl.textContent = "";
    this.errorEl.style.display = "none";
  }
}
